// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Helpers for displaying agents as review tracks.
 * trackId === agentId; everything else (label, slots, icon, color) comes from
 * the agent record. Style is chosen by index so the look is stable per workspace.
 */

import { Lock, Shield, Accessibility, Sparkles, type LucideIcon } from 'lucide-react'
import type { Agent } from '@/subsystems/agents'
import { getRendererComponent } from '@/app-shell/renderer-components'
import type { RendererDefinition } from '@/app-shell/types/workspace'
import type { DocumentListItem } from '@/subsystems/documents'
import type { ParsedAnalysisStatuses } from '@/applications/shared/hooks'

export interface TrackPalette {
  Icon: LucideIcon
  /** Color token used in `text-${token}` and `bg-${token}` */
  colorToken: string
  borderClass: string
  iconClass: string
  badgeClass: string
  bgClass: string
  borderTintClass: string
  printIcon: string
}

const PALETTE: TrackPalette[] = [
  {
    Icon: Lock,
    colorToken: 'chart-2',
    borderClass: 'border-l-4 border-l-chart-2',
    iconClass: 'text-chart-2',
    badgeClass: 'bg-chart-2/10 text-chart-2 border-chart-2/20',
    bgClass: 'bg-chart-2/10',
    borderTintClass: 'border-chart-2/30',
    printIcon: '🛡️',
  },
  {
    Icon: Shield,
    colorToken: 'chart-1',
    borderClass: 'border-l-4 border-l-chart-1',
    iconClass: 'text-chart-1',
    badgeClass: 'bg-chart-1/10 text-chart-1 border-chart-1/20',
    bgClass: 'bg-chart-1/10',
    borderTintClass: 'border-chart-1/30',
    printIcon: '🔒',
  },
  {
    Icon: Accessibility,
    colorToken: 'chart-3',
    borderClass: 'border-l-4 border-l-chart-3',
    iconClass: 'text-chart-3',
    badgeClass: 'bg-chart-3/10 text-chart-3 border-chart-3/20',
    bgClass: 'bg-chart-3/10',
    borderTintClass: 'border-chart-3/30',
    printIcon: '♿',
  },
  {
    Icon: Sparkles,
    colorToken: 'chart-4',
    borderClass: 'border-l-4 border-l-chart-4',
    iconClass: 'text-chart-4',
    badgeClass: 'bg-chart-4/10 text-chart-4 border-chart-4/20',
    bgClass: 'bg-chart-4/10',
    borderTintClass: 'border-chart-4/30',
    printIcon: '✨',
  },
  {
    Icon: Sparkles,
    colorToken: 'chart-5',
    borderClass: 'border-l-4 border-l-chart-5',
    iconClass: 'text-chart-5',
    badgeClass: 'bg-chart-5/10 text-chart-5 border-chart-5/20',
    bgClass: 'bg-chart-5/10',
    borderTintClass: 'border-chart-5/30',
    printIcon: '✨',
  },
]

export function trackPalette(index: number): TrackPalette {
  return PALETTE[index % PALETTE.length]
}

/**
 * Findings dataset key for an agent. Convention: `srm.{name}_findings`,
 * where {name} is the agentId with any trailing `-review` stripped and
 * dashes converted to underscores.
 */
export function findingsDatasetKey(agentId: string): string {
  const base = agentId.replace(/-review$/, '').replace(/-/g, '_')
  return `srm.${base}_findings`
}

/** Dataset keys bound to a renderer's dataset slots. */
export function rendererDatasetKeys(renderer: RendererDefinition): string[] {
  return renderer.slots
    .filter((s) => s.type === 'dataset' && s.datasetKey)
    .map((s) => s.datasetKey as string)
}

/**
 * Findings dataset keys actually bound to an agent's aggregate-mode renderer
 * slots — the same keys the track cards read. Falls back to the synthesized
 * default key for zero-config agents (mirroring TrackCard's default renderer).
 */
export function agentFindingsDatasetKeys(agent: Agent): string[] {
  const keys: string[] = []
  for (const r of Object.values(agent.renderers)) {
    const def = getRendererComponent(r.component)
    if (def?.versionMode !== 'aggregate') continue
    keys.push(...rendererDatasetKeys(r))
  }
  return keys.length > 0 ? keys : [findingsDatasetKey(agent.agentId)]
}

/** A document with stored results, at the version those results were written for. */
export interface RendererDocRef {
  documentId: string
  documentName?: string
  version: number
  /** The renderer datasets this document actually has results for. */
  datasetKeys: string[]
}

/** One stored dataset result, at the analyzed document version. */
export interface AnalyzedDatasetRef {
  documentId: string
  documentName?: string
  datasetKey: string
  version: number
}

/** The subset of a document needed to resolve renderer results. */
export type AnalyzableDocument = Pick<DocumentListItem, 'documentId' | 'name'>

/**
 * Resolve the exact document/dataset pairs recorded in analysis status.
 *
 * One agent can run different datasets against different upload slots. Status is the record of
 * which pair exists; forming the document × dataset cross-product creates expected 404s for the
 * pairs intentionally excluded by each node's slot filter.
 */
export function analyzedDatasetRefs(
  documents: AnalyzableDocument[],
  datasetKeys: string[],
  agentId: string,
  getDataset: (agentId: string, datasetKey: string) => ParsedAnalysisStatuses,
): AnalyzedDatasetRef[] {
  return documents.flatMap((document) =>
    datasetKeys.flatMap((datasetKey) => {
      const version = getDataset(agentId, datasetKey).versions.get(document.documentId)
      return version == null
        ? []
        : [{
            documentId: document.documentId,
            documentName: document.name,
            datasetKey,
            version,
          }]
    }),
  )
}

/**
 * The documents a renderer actually has results for, at their analyzed version.
 *
 * Analysis status already records which document carries which dataset and at which version,
 * so a panel fetches only pairs that exist. Fetching every document × dataset instead 404s for
 * each pair that never existed: one track holds documents analyzed by different nodes — the
 * intake form has the eligibility determination, the vendor agreement the terms findings — and
 * a dataset whose review has not run yet belongs to no document at all.
 */
export function renderedDocuments(
  renderer: RendererDefinition,
  documents: AnalyzableDocument[],
  agentId: string,
  getDataset: (agentId: string, datasetKey: string) => ParsedAnalysisStatuses,
): RendererDocRef[] {
  const refs: RendererDocRef[] = []
  for (const doc of documents) {
    const present = rendererDatasetKeys(renderer)
      .map((key) => ({ key, version: getDataset(agentId, key).versions.get(doc.documentId) }))
      .filter((entry): entry is { key: string; version: number } => entry.version != null)
    if (present.length === 0) continue
    refs.push({
      documentId: doc.documentId,
      documentName: doc.name,
      version: Math.max(...present.map((e) => e.version)),
      datasetKeys: present.map((e) => e.key),
    })
  }
  return refs
}
